import { useRef, useCallback } from 'react';
import { useFocusModeIntegration } from './useFocusModeIntegration';
import { useFocusActions } from './useFocusActions';
import { getAllConditionTaskIds } from '../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * useFlowchartCoreFocus Hook
 * 
 * Handles focus mode integration, conditions dialog state tracking,
 * and effective positions for selection box calculations.
 */
export function useFlowchartCoreFocus({
  tasks,
  nodePositions,
  nodeDimensions,
  edges,
  containerRef,
  pan,
  zoom,
  setPanUnconstrained,
  setZoom,
  onFocusModeChange,
  disableDragRef,
  isFocusModeRef,
  setSelectedNodes,
  selectedNodes,
  isMultiCampaignMode,
  statuses,
  initialFocusedTaskIds,
  effectivePositionsRef,
  handleConnectionComplete,
}) {
  // Track conditions when dialog opens to detect changes on close
  const conditionsDialogStateRef = useRef(null);
  // Ref for centerOnTask (defined later in focusMode hook)
  const centerOnTaskRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════
  // FOCUS MODE INTEGRATION
  // ═══════════════════════════════════════════════════════════════

  const focusMode = useFocusModeIntegration({
    tasks, nodePositions, nodeDimensions, edges, containerRef,
    pan, zoom, setPanUnconstrained, setZoom, onFocusModeChange,
    disableDragRef, isFocusModeRef, setSelectedNodes, isMultiCampaignMode,
    statuses, initialFocusedTaskIds,
  });

  const {
    focusedTaskIds, allFocusedTaskIds, hiddenConnections,
    displayTasks, displayPositions, displayEdges, focusContentBounds,
    handleFocusTask, handleFocusTasks, handleExitFocusMode, addToFocusedTasks,
    isFocusMode, centerOnTask, panToPosition,
    isAnimatingIntoFocus, isAnimatingOutOfFocus,
    positionsToUse, edgesToRender, tasksToRender, getNodeAnimationState,
    focusTasksInstant, exitFocusModeInstant,
  } = focusMode;

  // Focus actions
  const focusActions = useFocusActions({
    selectedNodes,
    setSelectedNodes,
    handleFocusTasks,
    handleFocusTask,
  });

  // Update centerOnTask ref for use in conditions dialog handlers
  centerOnTaskRef.current = centerOnTask;

  // ═══════════════════════════════════════════════════════════════
  // CONDITIONS DIALOG HANDLERS
  // ═══════════════════════════════════════════════════════════════

  // Called when conditions dialog opens - captures initial state
  const handleConditionsDialogOpen = useCallback((taskId) => {
    const task = tasks.find(t => t.id === taskId);
    conditionsDialogStateRef.current = {
      taskId,
      initialConditionTaskIds: new Set(
        getAllConditionTaskIds(task?.unlockConditions, tasks)
      ),
    };
  }, [tasks]);

  // Called when conditions dialog closes (Done clicked) - triggers auto-tidy if needed
  const handleConditionsDialogClose = useCallback((taskId) => {
    const dialogState = conditionsDialogStateRef.current;
    if (!dialogState || dialogState.taskId !== taskId) {
      conditionsDialogStateRef.current = null;
      return;
    }

    // Get current conditions (after all changes)
    const task = tasks.find(t => t.id === taskId);
    const currentConditionTaskIds = new Set(
      getAllConditionTaskIds(task?.unlockConditions, tasks)
    );

    // Find all task IDs that were added (new connections)
    const addedTaskIds = [...currentConditionTaskIds].filter(
      id => !dialogState.initialConditionTaskIds.has(id)
    );

    // Clear the dialog state
    conditionsDialogStateRef.current = null;

    // If any new connections were added, trigger auto-tidy on all involved chains
    if (addedTaskIds.length > 0) {
      // Small delay to let the state update propagate
      setTimeout(() => {
        // Trigger connection complete for all new connections at once
        // This finds all connected nodes and tidies them
        addedTaskIds.forEach(sourceId => {
          handleConnectionComplete(sourceId, taskId);
        });
      }, 50);
    }
  }, [tasks, handleConnectionComplete]);

  // ═══════════════════════════════════════════════════════════════
  // EFFECTIVE POSITIONS UPDATE
  // ═══════════════════════════════════════════════════════════════

  // Update effective positions ref for selection box and Start button calculations
  if (isFocusMode && allFocusedTaskIds && positionsToUse) {
    const focusedPositionsOnly = {};
    allFocusedTaskIds.forEach(taskId => {
      if (positionsToUse[taskId]) {
        focusedPositionsOnly[taskId] = positionsToUse[taskId];
      }
    });
    effectivePositionsRef.current = focusedPositionsOnly;
  } else {
    effectivePositionsRef.current = null;
  }

  return {
    // Focus mode state
    focusedTaskIds,
    allFocusedTaskIds,
    hiddenConnections,
    displayTasks,
    displayPositions,
    displayEdges,
    focusContentBounds,
    handleFocusTask: focusActions.handleFocusTaskWithClear,
    handleFocusTasks,
    handleExitFocusMode,
    addToFocusedTasks,
    isFocusMode,
    centerOnTask,
    panToPosition,
    isAnimatingIntoFocus,
    isAnimatingOutOfFocus,
    positionsToUse,
    edgesToRender,
    tasksToRender,
    getNodeAnimationState,
    focusSelectedTasks: focusActions.focusSelectedTasks,
    focusTasksInstant,
    exitFocusModeInstant,
    // Conditions dialog handlers
    handleConditionsDialogOpen,
    handleConditionsDialogClose,
    // Refs
    centerOnTaskRef,
  };
}

export default useFlowchartCoreFocus;
